import React from 'react';
import DataFlowDiagram from '../00-shared/DataFlowDiagram';
import MockClientProgress from '../00-shared/MockClientProgress';
import MockReadinessChart from '../00-shared/MockReadinessChart';
import MockDashboardPreview from '../00-shared/MockDashboardPreview';

interface ForHealthWellnessSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromLight: string;
            toGray: string;
        };
    };
}

const featureVisualizations: React.ReactNode[] = [
    <DataFlowDiagram variant="compact" className="py-2" />,
    <MockClientProgress variant="compact" className="py-2" />,
    <div className="border border-neutral-700 p-4">
        <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Client Readiness</div>
        <MockReadinessChart variant="compact" />
    </div>,
];

const ForHealthWellnessSection: React.FC<ForHealthWellnessSectionProps> = ({ highlightColor }) => {
    const features = [
        {
            title: 'Seamless Data Integration for Clients',
            desc: 'Easily add your clients\' wearable, testing, and performance data.',
        },
        {
            title: 'Track Client Progress',
            desc: 'Visualize each client\'s journey from initial assessment through milestones. Monitor mobility, strength, and recovery improvements over time.',
        },
        {
            title: 'Personal Trainers',
            desc: 'Push performance and optimize recovery through our adaptive data exploration models that account for your prescribed training.',
        }
    ];

    return (
        <section className="pb-32 px-4 sm:px-6 lg:px-8 bg-black relative notebook-grid overflow-hidden">
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="mb-12 observe-fade opacity-0 text-center">
                    <div className="flex items-center justify-center mb-4">
                        <MockDashboardPreview variant="icon" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Health and Wellness</h2>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                        <span className="text-white">
                            Power your Clients and Analytics
                        </span>
                    </h3>
                    <h4 className="text-xl md:text-2xl font-semibold text-gray-300 mb-6">
                        Your Intelligent Analytics Layer
                    </h4>
                    <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
                        Connect multiple wearable devices and get insights that help optimize your clients' performance and recovery. One platform for all their health and wellness data.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-4">
                    {features.map((feature, index) => (
                        <div key={index} className="bg-neutral-900 observe-fade opacity-0 text-center flex flex-col grid-accent" style={{ animationDelay: `${index * 0.2}s` }}>
                            <div className="p-4 border-b border-neutral-800 bg-neutral-900/50">
                                {featureVisualizations[index]}
                            </div>
                            <div className="p-6">
                                <h6 className="text-lg font-bold text-white mb-2">{feature.title}</h6>
                                <p className="text-gray-300">{feature.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ForHealthWellnessSection;

